import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { classService, examService, markService, sessionService } from '../../services/apiService';
import { showToast } from '../../utils/toast';

const Results = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [allExams, setAllExams] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch sessions and set active session as default
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await sessionService.getAll();
        const sessionsData = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
        setSessions(sessionsData);
        
        // Find and set active session as default
        const activeSession = sessionsData.find(s => s.is_active) || sessionsData[0] || null;
        if (activeSession) {
          setSelectedSession(activeSession.id);
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      }
    };
    fetchSessions();
  }, []);

  // Fetch classes and exams
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classesRes, examsRes] = await Promise.all([
          classService.getAll(),
          examService.getAll(),
        ]);
        const classesData = classesRes?.data || classesRes || [];
        const examsData = examsRes?.data || examsRes || [];
        setClasses(Array.isArray(classesData) ? classesData : []);
        setAllExams(Array.isArray(examsData) ? examsData : []);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load classes and exams');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter exams by selected session
  useEffect(() => {
    if (selectedSession && allExams.length > 0) {
      const filteredExams = allExams.filter(exam => exam.session_id === selectedSession);
      setExams(filteredExams);
      
      // Reset selected exam if it's not in the filtered list
      if (selectedExam && !filteredExams.find(e => e.id === selectedExam)) {
        setSelectedExam('');
      }
    } else {
      setExams([]);
      setSelectedExam('');
    }
  }, [selectedSession, allExams]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!selectedClass || !selectedExam) {
        setResults([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await markService.getAll({
          class_id: selectedClass,
          exam_id: selectedExam,
        });
        const resultsData = response?.data || response || [];
        setResults(Array.isArray(resultsData) ? resultsData : []);
      } catch (err) {
        console.error('Failed to load results:', err);
        setError('Failed to load results');
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [selectedClass, selectedExam]);

  const getPassFailStatus = (percentage) => {
    const isPass = percentage >= 40;
    return {
      label: isPass ? 'P' : 'F',
      bgColor: isPass ? '#1b5e20' : '#b71c1c', // Dark green / Dark red
      color: '#fff',
    };
  };

  const getSubjectColumns = () => {
    if (results.length === 0) return [];
    // Get all unique subjects from all students
    const allSubjects = new Set();
    results.forEach((result) => {
      result.subjects.forEach((subj) => {
        if (subj.name) {
          allSubjects.add(subj.name);
        }
      });
    });
    return Array.from(allSubjects).sort();
  };

  const subjectColumns = getSubjectColumns();

  // Sort results by percentage to identify top 3 students
  const sortedResults = [...results].sort((a, b) => {
    // Sort by percentage descending, then by total marks if percentage is same
    if (b.percentage !== a.percentage) {
      return b.percentage - a.percentage;
    }
    return b.total - a.total;
  });

  // Get top 3 student IDs
  const topThreeStudentIds = sortedResults.slice(0, 3).map(r => r.id);
  
  // Create a map for quick lookup
  const topThreeMap = {};
  topThreeStudentIds.forEach((id, index) => {
    topThreeMap[id] = index + 1; // 1 for 1st, 2 for 2nd, 3 for 3rd
  });

  const overallStats = results.length > 0 ? {
    totalStudents: results.length,
    averagePercentage: (results.reduce((sum, r) => sum + r.percentage, 0) / results.length).toFixed(2),
    passed: results.filter((r) => r.percentage >= 40).length,
    failed: results.filter((r) => r.percentage < 40).length,
    passPercentage: ((results.filter((r) => r.percentage >= 40).length / results.length) * 100).toFixed(2),
    highestPercentage: Math.max(...results.map((r) => r.percentage)).toFixed(2),
    lowestPercentage: Math.min(...results.map((r) => r.percentage)).toFixed(2),
  } : null;

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
              }}
            >
              Results Management
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Filters */}
            <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Session</InputLabel>
                    <Select
                      value={selectedSession}
                      onChange={(e) => setSelectedSession(e.target.value)}
                      label="Select Session"
                    >
                      {sessions.map((session) => (
                        <MenuItem key={session.id} value={session.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="body2">
                              {session.name || session.academic_year || session.id}
                            </Typography>
                            {session.is_active && (
                              <Chip 
                                label="Active" 
                                size="small" 
                                color="primary" 
                                sx={{ height: 20, fontSize: '0.65rem', ml: 'auto' }} 
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={[{ id: '', name: 'All Classes', section: null }, ...classes]}
                    getOptionLabel={(option) => {
                      if (!option || option.id === '') return 'All Classes';
                      return `Class ${option.name}${option.section ? ` - ${option.section}` : ''}`;
                    }}
                    value={selectedClass ? classes.find((cls) => cls.id === selectedClass) || null : { id: '', name: 'All Classes', section: null }}
                    onChange={(event, newValue) => {
                      setSelectedClass(newValue && newValue.id !== '' ? newValue.id : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Class"
                        placeholder="Search and select class..."
                        size="small"
                      />
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: 200,
                      },
                    }}
                    noOptionsText="No classes found"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Exam</InputLabel>
                    <Select
                      value={selectedExam}
                      onChange={(e) => setSelectedExam(e.target.value)}
                      label="Select Exam"
                      disabled={!selectedSession}
                    >
                      <MenuItem value="">
                        <em>All Exams</em>
                      </MenuItem>
                      {exams.map((exam) => (
                        <MenuItem key={exam.id} value={exam.id}>
                          {exam.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    fullWidth
                    onClick={() => navigate('/results/add')}
                    size="small"
                    sx={{ 
                      height: { xs: '36px', sm: '40px' },
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    Add Results
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Overall Statistics */}
            {overallStats && (
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
                <Grid item xs={6} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Total Students
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700,
                          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                        }}
                      >
                        {overallStats.totalStudents}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Students Passed
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'success.main',
                          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                        }}
                      >
                        {overallStats.passed}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Average Percentage
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'primary.main',
                          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                        }}
                      >
                        {overallStats.averagePercentage}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Highest / Lowest
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                        }}
                      >
                        <Box component="span" sx={{ color: 'success.main' }}>{overallStats.highestPercentage}%</Box>
                        {' / '}
                        <Box component="span" sx={{ color: 'error.main' }}>{overallStats.lowestPercentage}%</Box>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Results Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : !selectedClass || !selectedExam ? (
              <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  Please select a class and exam to view results.
                </Typography>
              </Paper>
            ) : results.length === 0 ? (
              <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  No results found for the selected class and exam.
                </Typography>
              </Paper>
            ) : (
              <TableContainer 
                component={Paper} 
                elevation={1} 
                sx={{ 
                  borderRadius: 2,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: { xs: '70vh', md: '75vh' },
                  width: '100%',
                  minWidth: '100%',
                  WebkitOverflowScrolling: 'touch',
                  scrollBehavior: 'smooth',
                  '&::-webkit-scrollbar': {
                    height: 10,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: 5,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: 5,
                    border: '2px solid',
                    borderColor: 'grey.100',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#9ca3af #f3f4f6',
                }}
              >
                <Table sx={{ 
                  width: '100%',
                  minWidth: '100%',
                  tableLayout: 'auto',
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                    padding: { xs: '8px 4px', sm: '12px 8px', md: '16px' },
                    whiteSpace: { xs: 'nowrap', md: 'normal' },
                  },
                }}>
                  <TableHead sx={{ position: 'sticky', top: 0, zIndex: 4 }}>
                    <TableRow sx={{ bgcolor: 'primary.main', '& th': { fontWeight: 700, color: 'white', backgroundColor: 'primary.main' } }}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Roll No</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }} align="center">Rank</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Student Name</TableCell>
                      {subjectColumns.map((subject) => (
                        <TableCell 
                          key={subject} 
                          align="center"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                        >
                          {subject}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Total</TableCell>
                      <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Percentage</TableCell>
                      <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((result, idx) => {
                      const rank = topThreeMap[result.id];
                      return (
                        <TableRow key={result.id} hover>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>{result.roll_number}</TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                          {rank && (
                            <EmojiEventsRoundedIcon 
                              sx={{ 
                                color: rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32', // Gold, Silver, Bronze
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                              }} 
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                          {result.student_name}
                        </TableCell>
                        {subjectColumns.map((subject) => {
                          const subj = result.subjects.find((s) => s.name === subject);
                          if (!subj || subj.marks === null || subj.marks === undefined) {
                            return (
                              <TableCell 
                                key={subject} 
                                align="center"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}
                              >
                                -
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell 
                              key={subject} 
                              align="center"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}
                            >
                              {subj.marks}/{subj.max_marks}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                          {result.max_total > 0 ? `${result.total}/${result.max_total}` : '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' } }}>
                          {result.max_total > 0 ? `${result.percentage.toFixed(2)}%` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {result.max_total > 0 ? (() => {
                            const status = getPassFailStatus(result.percentage);
                            return (
                              <Chip
                                label={status.label}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: status.bgColor,
                                  color: status.color,
                                  minWidth: 36,
                                  fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                }}
                              />
                            );
                          })() : (
                            <Chip
                              label="-"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: '#e0e0e0',
                                color: '#666',
                                minWidth: 36,
                                fontSize: { xs: '0.7rem', sm: '0.875rem' }
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Results;
